// ── La conversation avec l'assistant, dans le navigateur (chantier ASI) ──
// Gardee par le SupportProvider jusqu'au rechargement de la page (A10) ; rien n'est enregistre.
import { ASSISTANT_LIMITS, type AssistantMessage } from '@/lib/schemas/assistant.schema';
import type { AssistantEvent } from '@/lib/services/assistant.service';

export interface Source { slug: string; title: string }

export interface ConversationMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  /** Une reponse : en attente du premier mot, en cours d'ecriture, finie, arretee, interrompue, ou modele indisponible */
  state?: 'waiting' | 'writing' | 'done' | 'stopped' | 'interrupted' | 'unavailable';
  sources?: Source[];
  /** « C'est resolu » (A9) */
  resolved?: boolean;
}

export interface Conversation {
  messages: ConversationMessage[];
  /** idle : pret ; thinking : la question est partie ; waking (A7) : le modele se reveille, wakingLong apres 2 min 30 */
  status: 'idle' | 'thinking' | 'waking' | 'wakingLong' | 'writing' | 'limit';
  /** A11 : le message de la limite du jour */
  limitMessage?: string;
  nextId: number;
}

export const EMPTY_CONVERSATION: Conversation = { messages: [], status: 'idle', nextId: 1 };

export type AssistantAction =
  | { type: 'ask'; question: string }
  | { type: 'event'; event: AssistantEvent }
  | { type: 'wakeLong' }
  | { type: 'stop' }
  | { type: 'limit'; message: string }
  | { type: 'resolved'; id: number }
  | { type: 'reset' };

/** La reponse en cours : le dernier message, s'il vient de l'assistant et n'est pas fini */
const updateAnswer = (s: Conversation, patch: (m: ConversationMessage) => ConversationMessage): Conversation => {
  const last = s.messages.at(-1);
  if (!last || last.role !== 'assistant') return s;
  return { ...s, messages: [...s.messages.slice(0, -1), patch(last)] };
};

const busy = (s: Conversation) => s.status === 'thinking' || s.status === 'waking' || s.status === 'wakingLong' || s.status === 'writing';

export function assistantReducer(s: Conversation, a: AssistantAction): Conversation {
  switch (a.type) {
    case 'ask':
      return {
        ...s,
        status: 'thinking',
        nextId: s.nextId + 2,
        messages: [...s.messages, { id: s.nextId, role: 'user', text: a.question }, { id: s.nextId + 1, role: 'assistant', text: '', state: 'waiting' }],
      };
    case 'wakeLong':
      return s.status === 'waking' ? { ...s, status: 'wakingLong' } : s;
    case 'stop':
      return busy(s) ? { ...updateAnswer(s, m => ({ ...m, state: 'stopped' })), status: 'idle' } : s;
    case 'limit':
      return { ...s, status: 'limit', limitMessage: a.message, messages: s.messages.filter(m => !(m.role === 'assistant' && m.state === 'waiting')) };
    case 'resolved':
      return { ...s, messages: s.messages.map(m => (m.id === a.id ? { ...m, resolved: true } : m)) };
    case 'reset':
      return s.status === 'limit' ? { ...EMPTY_CONVERSATION, status: 'limit', limitMessage: s.limitMessage } : EMPTY_CONVERSATION;
    case 'event': {
      if (!busy(s)) return s;
      const e = a.event;
      if (e.t === 'reveil') return { ...s, status: 'waking' };
      if (e.t === 'texte') return { ...updateAnswer(s, m => ({ ...m, text: m.text + e.d, state: 'writing' })), status: 'writing' };
      if (e.t === 'sources') return updateAnswer(s, m => ({ ...m, sources: e.items }));
      if (e.t === 'fin') return { ...updateAnswer(s, m => ({ ...m, state: 'done' })), status: 'idle' };
      return { ...updateAnswer(s, m => ({ ...m, state: e.code === 'interrompu' ? 'interrupted' : 'unavailable' })), status: 'idle' };
    }
  }
}

/** A11 : une question de 1 a 1 000 caracteres, et aucune reponse en route ni limite atteinte */
export function canSend(question: string, s: Conversation): boolean {
  const n = question.trim().length;
  return n > 0 && n <= ASSISTANT_LIMITS.questionMaxChars && s.status === 'idle';
}

/** Les messages precedents a renvoyer avec la question : les questions et les reponses ecrites */
export function historyOf(s: Conversation): AssistantMessage[] {
  return s.messages
    .filter(m => m.role === 'user' || (m.text && m.state !== 'waiting'))
    .filter((m, i, all) => !(m.role === 'user' && i === all.length - 1))
    .map(m => ({ role: m.role, text: m.text.slice(0, ASSISTANT_LIMITS.historyMessageMaxChars) }))
    .slice(-ASSISTANT_LIMITS.historyMaxMessages);
}
